import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { concatMap, from, map, Observable, of, switchMap, tap, toArray } from 'rxjs';

import { ApiPaginatedResponseDto } from '@app/api/common-dtos';
import { ImGroupDeviceDto, ImGroupDto } from '@app/api/im/dtos';
import { ImGroupApiService } from '@app/api/im/im-group-api.service';
import { loadAllPaginatedItems } from '@app/shared/utils/paginated-request.utils';

import { ImGroupStateActions } from './im-group.state.actions';

export interface ImGroupStateItem {
  groupId: string;
  group: ImGroupDto;
  members: ImGroupDeviceDto[];
}

type ImGroupStateModel = ImGroupStateItem[];

function sortStateItemsPredicate(a: ImGroupStateItem, b: ImGroupStateItem): number {
  return a.group.name >= b.group.name ? 1 : -1;
}

@State<ImGroupStateModel>({
  name: 'imGroups',
  defaults: [],
})
@Injectable()
export class ImGroupState {
  @Selector()
  public static groups(state: ImGroupStateModel): ImGroupStateItem[] {
    return state;
  }

  constructor(private readonly imGroupApi: ImGroupApiService) {}

  @Action(ImGroupStateActions.Load, { cancelUncompleted: true })
  public load({ setState }: StateContext<ImGroupStateModel>): Observable<ImGroupStateItem[]> {
    return loadAllPaginatedItems((request) =>
      this.imGroupApi.find(request).pipe(concatMap((response) => this.enrichGroupsBatch(response))),
    ).pipe(tap((groups) => setState(groups)));
  }

  @Action(ImGroupStateActions.ReloadGroup)
  public reloadGroup(
    { getState, setState }: StateContext<ImGroupStateModel>,
    { groupId }: ImGroupStateActions.ReloadGroup,
  ): Observable<ImGroupStateItem | null> {
    return this.imGroupApi.findOne(groupId).pipe(
      switchMap((group) => (group ? this.enrichGroup(group) : of(null))),
      tap((group) => {
        if (!group) {
          return;
        }

        const state = [...getState()];
        const groupIndex = state.findIndex((i) => i.groupId === groupId);

        if (groupIndex >= 0) {
          state.splice(groupIndex, 1, group);
        } else {
          state.push(group);
        }

        state.sort(sortStateItemsPredicate);
        setState(state);
      }),
    );
  }

  @Action(ImGroupStateActions.LoadGroupIfNotLoaded)
  public loadGroupIfNotLoaded(
    { getState, dispatch }: StateContext<ImGroupStateModel>,
    { groupId }: ImGroupStateActions.LoadGroupIfNotLoaded,
  ): void {
    const state = getState();

    if (!state.find((i) => i.groupId === groupId)) {
      dispatch(new ImGroupStateActions.ReloadGroup(groupId));
    }
  }

  @Action(ImGroupStateActions.DeleteGroup)
  public deleteGroup(
    { getState, setState }: StateContext<ImGroupStateModel>,
    { groupId }: ImGroupStateActions.DeleteGroup,
  ): void {
    const state = [...getState()];
    const index = state.findIndex((i) => i.groupId === groupId);

    if (index >= 0) {
      state.splice(index, 1);
      setState(state);
    }
  }

  @Action(ImGroupStateActions.DeleteGroupMember)
  public deleteGroupMember(
    { getState, setState }: StateContext<ImGroupStateModel>,
    { groupId, memberId }: ImGroupStateActions.DeleteGroupMember,
  ): void {
    const state = [...getState()];
    const groupIndex = state.findIndex((i) => i.groupId === groupId);

    if (groupIndex >= 0) {
      const group = state[groupIndex];
      const members = [...group.members];
      const memberIndex = members.findIndex((member) => member.deviceId == memberId);

      if (memberIndex >= 0) {
        members.splice(memberIndex, 1);
      }

      state.splice(groupIndex, 1, {
        ...group,
        members,
      });

      setState(state);
    }
  }

  protected enrichGroupsBatch(
    response: ApiPaginatedResponseDto<ImGroupDto>,
  ): Observable<ApiPaginatedResponseDto<ImGroupStateItem>> {
    if (!response.rows.length) {
      return of({
        rows: [],
        total: response.total,
      });
    }

    return from(response.rows).pipe(
      concatMap((group) => this.enrichGroup(group)),
      toArray(),
      map((data) => ({
        rows: data.sort(sortStateItemsPredicate),
        total: response.total,
      })),
    );
  }

  protected enrichGroup(group: ImGroupDto): Observable<ImGroupStateItem> {
    return this.imGroupApi.getGroupDevices(group.id).pipe(
      map(
        (response): ImGroupStateItem => ({
          groupId: group.id,
          group: group,
          members: response.devices,
        }),
      ),
    );
  }
}
